/**
 * Is the shop open, and what day should we suggest?
 *
 * Everything here reads the posted hours from shop.config.json and evaluates
 * them in the SHOP's timezone, never the visitor's. A customer in California
 * at 7am is looking at a shop that opened two hours ago; `new Date().getHours()`
 * would call it closed. Same reason Hero computes its weekday through Intl
 * rather than the local clock.
 *
 * Opening hours are config, not constants. Hardcoding 9-18 weekdays and 9-16
 * Saturday means the card keeps saying "Open today" the first time Kenny
 * changes the posted hours and forgets this file exists.
 */

export type HoursRow = { day: string; open: string | null; close: string | null };

const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Weekday index (0=Sun) where the shop is, not where the visitor is. */
export function shopWeekdayIndex(tz: string, now = new Date()): number {
  const name = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: tz }).format(now);
  return DAY_KEYS.indexOf(name as (typeof DAY_KEYS)[number]);
}

/** Minutes past midnight in the shop's timezone. */
function shopMinutes(tz: string, now = new Date()): number {
  const [h, m] = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: tz,
  })
    .format(now)
    .split(':')
    .map(Number);
  return h * 60 + m;
}

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

export function rowFor(hours: ReadonlyArray<HoursRow>, dayIndex: number) {
  return hours.find((h) => h.day === DAY_KEYS[dayIndex % 7]);
}

/** Within today's posted window, evaluated shop-local. */
export function isShopOpenNow(
  hours: ReadonlyArray<HoursRow>,
  tz: string,
  now = new Date()
): boolean {
  const row = rowFor(hours, shopWeekdayIndex(tz, now));
  if (!row?.open || !row.close) return false;
  const mins = shopMinutes(tz, now);
  return mins >= toMinutes(row.open) && mins < toMinutes(row.close);
}

/** Locale weekday name for a day `ahead` days from now, in the shop's zone. */
export function weekdayNameAt(locale: string, tz: string, ahead: number, now = new Date()): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: tz }).format(
    new Date(now.getTime() + ahead * 86_400_000)
  );
}

/**
 * The next day the shop opens, looking forward from tomorrow.
 * `ahead === 1` means literally tomorrow; callers pick "tomorrow" wording
 * for that and a weekday name otherwise, so Saturday night resolves to
 * Monday instead of a tomorrow that is closed.
 */
export function nextOpenDay(
  hours: ReadonlyArray<HoursRow>,
  tz: string,
  now = new Date()
): { ahead: number; open: string } | null {
  const idx = shopWeekdayIndex(tz, now);
  for (let ahead = 1; ahead <= 7; ahead++) {
    const row = rowFor(hours, idx + ahead);
    if (row?.open) return { ahead, open: row.open };
  }
  return null;
}

/**
 * "today" / "tomorrow" / a weekday name, for "a sharper plan for ___".
 *
 * Noon is the pivot, per the brief: past midday the realistic next visit is
 * the following open day, even though the shop is still serving. Which day
 * that is comes from the posted hours, so a closed Sunday resolves to Monday
 * without either day being named in code.
 */
export function getFriendlyDay(
  locale: string,
  hours: ReadonlyArray<HoursRow>,
  tz: string,
  labels: { today: string; tomorrow: string },
  now = new Date()
): string {
  const dow = shopWeekdayIndex(tz, now);
  const openToday = Boolean(rowFor(hours, dow)?.open);
  const beforeNoon = shopMinutes(tz, now) < 12 * 60;

  if (openToday && beforeNoon) return labels.today;

  const next = nextOpenDay(hours, tz, now);
  if (!next) return labels.today;
  return next.ahead === 1 ? labels.tomorrow : weekdayNameAt(locale, tz, next.ahead, now);
}

/**
 * Today's posted window in shop-local terms, if today is an open day.
 * `stillAhead` is true until closing time — before opening counts, because
 * "the chairs open when the doors do" is exactly the before-opening message.
 */
export function todayWindow(
  hours: ReadonlyArray<HoursRow>,
  tz: string,
  now = new Date()
): { open: string; close: string; stillAhead: boolean } | null {
  const row = rowFor(hours, shopWeekdayIndex(tz, now));
  if (!row?.open || !row.close) return null;
  return { open: row.open, close: row.close, stillAhead: shopMinutes(tz, now) < toMinutes(row.close) };
}
